import { z } from 'zod'

import type { CustomerWriteRequest, NextOfKinInput } from '@/api/types'

import { CONSENT_TEXT_VERSION, GENDERS, MARITAL_STATUSES, REGIONS } from './constants'

/**
 * Client-side validation for customer capture.
 *
 * Everything here mirrors a rule the server also enforces, and none of it replaces one. The Ghana
 * Card check character, uniqueness across the institution, and the minimum age are all decided by
 * the API against data this browser cannot see; what these checks buy is a correction made while
 * the customer is still at the counter rather than after a round trip.
 */

/** `GHA-XXXXXXXXX-X`. The trailing character is a check digit the server verifies. */
const GHANA_CARD_SHAPE = /^GHA-\d{9}-\d$/

/** GhanaPost digital address, e.g. `GA-183-8164`. */
const GHANA_POST_GPS_SHAPE = /^[A-Z]{2}-\d{3,4}-\d{4}$/

/**
 * A Ghanaian mobile number in local or international form. The network digit after the trunk
 * prefix is 2, 3 or 5 across every operator currently allocated.
 */
const GHANA_PHONE_SHAPE = /^(?:\+233|0)[235]\d{8}$/

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/** The floor for an account in a personal name. The server refuses anything below it. */
export const MINIMUM_AGE_YEARS = 18

/**
 * Lays out a Ghana Card number as it is typed, without ever refusing a keystroke.
 *
 * Operators read these off a card and type them as a run of digits; forcing them to key the
 * prefix and the dashes is nine wasted keystrokes per customer, dozens of times a day.
 */
export function formatGhanaCardInput(raw: string): string {
  const compact = raw.toUpperCase().replace(/[^A-Z0-9]/g, '')
  const digits = compact.replace(/[^0-9]/g, '').slice(0, 10)

  if (digits.length === 0) {
    // Nothing to lay out yet, so the prefix stays exactly as typed — which is also what lets the
    // field be cleared one backspace at a time.
    return compact.slice(0, 3)
  }

  const serial = digits.slice(0, 9)
  const checkDigit = digits.slice(9)

  return checkDigit.length > 0 ? `GHA-${serial}-${checkDigit}` : `GHA-${serial}`
}

export const isGhanaCardShape = (value: string) => GHANA_CARD_SHAPE.test(value.trim().toUpperCase())

/**
 * Strips the punctuation people type into a phone field and puts an international number into the
 * `+233…` form. The server normalises again before it judges uniqueness — `0241234567` and
 * `+233241234567` are one person — so this only decides what the shape check sees.
 */
export function normalisePhoneInput(raw: string): string {
  const compact = raw.replace(/[\s()\-.]/g, '')

  if (compact.startsWith('00233')) {
    return `+${compact.slice(2)}`
  }

  if (compact.startsWith('233')) {
    return `+${compact}`
  }

  return compact
}

export const isGhanaPhoneShape = (value: string) => GHANA_PHONE_SHAPE.test(normalisePhoneInput(value))

export const formatGhanaPostGpsInput = (raw: string) => raw.toUpperCase().replace(/[^A-Z0-9-]/g, '')

/**
 * The latest date of birth that still clears the minimum age.
 *
 * The browser clock decides nothing of business consequence in this application — the ledger owns
 * the business date — but an age check made before the request leaves is a courtesy, and the
 * server refuses an underage record whatever this says.
 */
export function latestPermittedBirthDate(today: Date = new Date()): string {
  const iso = today.toISOString().slice(0, 10)
  const [year, month, day] = iso.split('-')
  return `${String(Number(year) - MINIMUM_AGE_YEARS)}-${month}-${day}`
}

const optionalText = z.string().trim()

const addressSchema = z.object({
  ghanaPostGps: z
    .string()
    .trim()
    .min(1, 'Enter the GhanaPost digital address.')
    .regex(GHANA_POST_GPS_SHAPE, 'Use the GhanaPost form, for example GA-183-8164.'),
  streetOrArea: z.string().trim().min(1, 'Enter the street, house number, or area.'),
  landmark: optionalText,
  town: z.string().trim().min(1, 'Enter the town or suburb.'),
  district: z.string().trim().min(1, 'Enter the district.'),
  // Expressed as "a string that must be one of these" rather than a bare enum so that the empty
  // choice a select starts on is a value the form can hold. Without it the control would begin
  // life uncontrolled and become controlled on the operator's first choice.
  region: z.string().min(1, 'Select a region.').pipe(z.enum(REGIONS)),
})

const blankAddressSchema = z.object({
  ghanaPostGps: optionalText,
  streetOrArea: optionalText,
  landmark: optionalText,
  town: optionalText,
  district: optionalText,
  region: z.string().trim(),
})

const nextOfKinRowSchema = z.object({
  fullName: optionalText,
  relationship: z.string().trim(),
  phoneNumber: optionalText,
  address: optionalText,
})

export type NextOfKinRow = z.infer<typeof nextOfKinRowSchema>

const rowIsBlank = (row: NextOfKinRow) =>
  [row.fullName, row.relationship, row.phoneNumber, row.address].every(
    (value) => value.trim().length === 0,
  )

export const customerWizardSchema = z
  .object({
    branchId: z.string().min(1, 'Select the branch that will hold this record.'),

    firstName: z.string().trim().min(1, 'Enter the first name as it appears on the Ghana Card.'),
    middleName: optionalText,
    lastName: z.string().trim().min(1, 'Enter the surname as it appears on the Ghana Card.'),
    gender: z
      .string()
      .min(1, 'Select the gender recorded on the Ghana Card.')
      .pipe(z.enum(GENDERS)),
    dateOfBirth: z
      .string()
      .min(1, 'Enter the date of birth.')
      .refine((value) => value <= latestPermittedBirthDate(), {
        message: `An account holder must be at least ${String(MINIMUM_AGE_YEARS)} years old.`,
      }),
    placeOfBirth: z.string().trim().min(1, 'Enter the place of birth.'),
    nationality: z.string().trim().min(2, 'Enter the nationality.'),
    maritalStatus: z.string().min(1, 'Select a marital status.').pipe(z.enum(MARITAL_STATUSES)),

    ghanaCardNumber: z
      .string()
      .trim()
      .min(1, 'A Ghana Card is required.')
      .regex(GHANA_CARD_SHAPE, 'Use the form GHA-123456789-0.'),
    ghanaCardIssueDate: optionalText,
    ghanaCardExpiryDate: optionalText,

    phoneNumber: z
      .string()
      .trim()
      .min(1, 'Enter a mobile number.')
      .refine(isGhanaPhoneShape, { message: 'Not a recognisable Ghanaian mobile number.' }),
    altPhoneNumber: optionalText.refine((value) => value === '' || isGhanaPhoneShape(value), {
      message: 'Not a recognisable Ghanaian mobile number.',
    }),
    email: optionalText.refine((value) => value === '' || EMAIL_SHAPE.test(value), {
      message: 'Enter a complete email address, or leave it empty.',
    }),

    occupation: z.string().trim().min(1, 'Enter the occupation.'),
    employerOrBusinessName: optionalText,
    monthlyIncomeEstimateMinor: z
      .number({ message: 'Enter an estimated monthly income.' })
      .int()
      .min(0, 'An income estimate cannot be negative.'),

    residence: addressSchema,
    hasBusinessAddress: z.boolean(),
    business: blankAddressSchema,

    nextOfKin: z.array(nextOfKinRowSchema),

    consentAccountOperation: z.boolean().refine((granted) => granted, {
      message: 'Account-operation consent is the basis for holding this record and must be captured.',
    }),
    consentCreditBureau: z.boolean(),
    consentMarketing: z.boolean(),
    consentBiometricCapture: z.boolean(),
  })
  .superRefine((values, context) => {
    if (values.hasBusinessAddress) {
      const parsed = addressSchema.safeParse(values.business)
      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          context.addIssue({
            code: 'custom',
            path: ['business', ...issue.path],
            message: issue.message,
          })
        }
      }
    }

    values.nextOfKin.forEach((row, index) => {
      // A row left entirely blank is simply not a next of kin, and is dropped on submission. A row
      // that was started, though, has to be finished — a name with no way to reach the person is
      // worse than no entry at all.
      if (rowIsBlank(row)) {
        return
      }

      if (row.fullName.trim().length === 0) {
        context.addIssue({
          code: 'custom',
          path: ['nextOfKin', index, 'fullName'],
          message: 'Enter the full name, or clear this entry.',
        })
      }

      if (row.relationship.trim().length === 0) {
        context.addIssue({
          code: 'custom',
          path: ['nextOfKin', index, 'relationship'],
          message: 'Select the relationship.',
        })
      }

      if (!isGhanaPhoneShape(row.phoneNumber)) {
        context.addIssue({
          code: 'custom',
          path: ['nextOfKin', index, 'phoneNumber'],
          message: 'Not a recognisable Ghanaian mobile number.',
        })
      }
    })
  })

/**
 * Two views of the same form. The input type is what the controls hold while capture is in
 * progress — an unmade choice is an empty string, because that is what a select can render. The
 * output type is what a completed capture parses to, with every choice narrowed to the value the
 * API accepts.
 */
export type CustomerWizardInput = z.input<typeof customerWizardSchema>
export type CustomerWizardValues = z.output<typeof customerWizardSchema>

const orNull = (value: string | undefined) => {
  const trimmed = value?.trim() ?? ''
  return trimmed.length > 0 ? trimmed : null
}

/**
 * Builds the payload from what was captured.
 *
 * The review step renders the result of this function rather than the form state, so what an
 * operator confirms is literally what is sent — not a second rendering of the same values that
 * could drift from it.
 */
export function toCustomerWriteRequest(values: CustomerWizardValues): CustomerWriteRequest {
  const nextOfKin: NextOfKinInput[] = values.nextOfKin
    .filter((row) => !rowIsBlank(row))
    .map((row) => ({
      fullName: row.fullName.trim(),
      relationship: row.relationship.trim(),
      phoneNumber: normalisePhoneInput(row.phoneNumber),
      address: row.address.trim(),
    }))

  return {
    branchId: values.branchId,
    firstName: values.firstName.trim(),
    middleName: orNull(values.middleName),
    lastName: values.lastName.trim(),
    gender: values.gender,
    dateOfBirth: values.dateOfBirth,
    placeOfBirth: values.placeOfBirth.trim(),
    nationality: values.nationality.trim().toUpperCase(),
    maritalStatus: values.maritalStatus,
    phoneNumber: normalisePhoneInput(values.phoneNumber),
    altPhoneNumber: values.altPhoneNumber.trim() === '' ? null : normalisePhoneInput(values.altPhoneNumber),
    email: orNull(values.email),
    ghanaCardNumber: values.ghanaCardNumber.trim().toUpperCase(),
    occupation: values.occupation.trim(),
    employerOrBusinessName: orNull(values.employerOrBusinessName),
    monthlyIncomeEstimateMinor: values.monthlyIncomeEstimateMinor,
    addresses: [
      {
        addressType: 'Residence',
        ghanaPostGps: values.residence.ghanaPostGps.trim().toUpperCase(),
        streetOrArea: values.residence.streetOrArea.trim(),
        landmark: orNull(values.residence.landmark),
        town: values.residence.town.trim(),
        district: values.residence.district.trim(),
        region: values.residence.region,
        // Coordinates are recorded by the field application against a real fix. A figure typed at
        // a back-office desk would be evidence of nothing.
        latitude: null,
        longitude: null,
        isCurrent: true,
      },
      ...(values.hasBusinessAddress
        ? [
            {
              addressType: 'Business' as const,
              ghanaPostGps: values.business.ghanaPostGps.trim().toUpperCase(),
              streetOrArea: values.business.streetOrArea.trim(),
              landmark: orNull(values.business.landmark),
              town: values.business.town.trim(),
              district: values.business.district.trim(),
              region: values.business.region,
              latitude: null,
              longitude: null,
              isCurrent: true,
            },
          ]
        : []),
    ],
    identityDocuments: [
      {
        idType: 'GhanaCard',
        idNumber: values.ghanaCardNumber.trim().toUpperCase(),
        issueDate: orNull(values.ghanaCardIssueDate),
        expiryDate: orNull(values.ghanaCardExpiryDate),
        isPrimary: true,
        // Scans are attached to the record after it exists, through the document endpoints.
        frontImageId: null,
        backImageId: null,
      },
    ],
    nextOfKin,
    consents: [
      {
        consentType: 'AccountOperation',
        granted: values.consentAccountOperation,
        textVersion: CONSENT_TEXT_VERSION,
      },
      {
        consentType: 'CreditBureau',
        granted: values.consentCreditBureau,
        textVersion: CONSENT_TEXT_VERSION,
      },
      { consentType: 'Marketing', granted: values.consentMarketing, textVersion: CONSENT_TEXT_VERSION },
      {
        consentType: 'BiometricCapture',
        granted: values.consentBiometricCapture,
        textVersion: CONSENT_TEXT_VERSION,
      },
    ],
  }
}
